// src/plugins/sales-forecast/components/EditInputsDrawer.tsx
import React, { useCallback, useState } from "react";
import pluginConfig from "../sales-plugin.json";
import { Home, ClipboardList, TrendingUp, BarChart3 } from "lucide-react";
import { Params } from "./ControlPanel";
import type { CloneEntry } from "./CloneNeighborhoodControl";
import { PriceRangeControl } from "./PriceRangeControl";
import { SquareFootRangeControl } from "./SquareFootRangeControl";
import { GARAGE_TYPE_OPTIONS } from "./GarageTypeControl";
import { CITY_OPTIONS } from "./CityControl";
import { AmenityControl } from "./AmenityControl";

interface EditInputsDrawerProps {
  open: boolean;
  onClose: () => void;
  onForecast: () => void;
  params: Params;
  setParams: React.Dispatch<React.SetStateAction<Params>>;
  resetDefaults: () => void;
  selectedClone: CloneEntry | null;
  clusterName: string;
  clusterDescription: string;
}

// ── shared theme tokens ───────────────────────────────────────────────────────
const cardBg = "var(--card-bg)";
const subCardBg = "var(--subcard-bg)";
const borderColor = "var(--border-color)";
const textColor = "var(--text-color)";
const mutedText = "var(--muted-text-color)";
const inputBg = "var(--input-bg)";
const accentColor = "var(--accent-color)";

// ── small building blocks ─────────────────────────────────────────────────────

const sectionLabel: React.CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: mutedText,
};

const numberInputStyle: React.CSSProperties = {
  width: "100%",
  backgroundColor: inputBg,
  color: textColor,
  border: `1px solid ${borderColor}`,
  borderRadius: "8px",
  padding: "0.55rem 0.7rem",
  fontSize: "0.95rem",
  fontWeight: 600,
};

const selectFieldStyle: React.CSSProperties = {
  ...numberInputStyle,
  cursor: "pointer",
};

/** Titled card with an icon, heading, divider, and an optional reset button. */
const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  onReset?: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ icon, title, onReset, style, children }) => (
  <div
    style={{
      backgroundColor: subCardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: "14px",
      padding: "1.25rem",
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
      minWidth: 0,
      ...style,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
      <span style={{ color: accentColor, display: "inline-flex" }}>{icon}</span>
      <span
        style={{
          flex: 1,
          fontSize: "0.95rem",
          fontWeight: 800,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: textColor,
          lineHeight: 1.15,
        }}
      >
        {title}
      </span>
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          aria-label="Reset"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2rem",
            height: "2rem",
            borderRadius: "10px",
            border: `1px solid ${borderColor}`,
            backgroundColor: "var(--subcard-bg)",
            color: mutedText,
            cursor: "pointer",
            flexShrink: 0,
            boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", flexShrink: 0 }}>
            <path
              d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
              stroke="var(--text-color)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 3v5h5"
              stroke="var(--text-color)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
    <div style={{ borderTop: `1px solid ${borderColor}`, margin: "-0.25rem 0" }} />
    {children}
  </div>
);

/** Compact "LABEL ········ value" + filled slider row. */
const SliderRow: React.FC<{
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}> = ({ label, value, display, min, max, step, onChange }) => {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "0.5rem" }}>
        <span style={sectionLabel}>{label}</span>
        <span style={{ fontSize: "0.7rem", fontWeight: 800, color: accentColor, whiteSpace: "nowrap" }}>
          {display}
        </span>
      </div>
      <input
        type="range"
        className="mf-range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{
          background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${pct}%, var(--track-bg-color) ${pct}%, var(--track-bg-color) 100%)`,
        }}
      />
    </div>
  );
};

/** Label + native select rendering numeric options. */
const NumberSelect: React.FC<{
  label: string;
  value: number;
  options: number[];
  onChange: (v: number) => void;
}> = ({ label, value, options, onChange }) => {
  const opts = options.includes(value) ? options : [value, ...options];
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", minWidth: 0 }}>
      <span style={sectionLabel}>{label}</span>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} style={selectFieldStyle}>
        {opts.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
};

/** Label + native select rendering string options. */
const TextSelect: React.FC<{
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => {
  const opts = options.includes(value) ? options : [value, ...options];
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", minWidth: 0 }}>
      <span style={sectionLabel}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={selectFieldStyle}>
        {opts.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
};

/** Label + number input. */
const NumberField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  step?: number;
  prefix?: string;
}> = ({ label, value, onChange, min, step, prefix }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", minWidth: 0 }}>
    <span style={sectionLabel}>{label}</span>
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      {prefix && (
        <span style={{ position: "absolute", left: "0.7rem", color: mutedText, fontWeight: 600, pointerEvents: "none" }}>
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={Number.isFinite(value) ? value : ""}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ ...numberInputStyle, paddingLeft: prefix ? "1.6rem" : numberInputStyle.padding }}
      />
    </div>
  </label>
);

// ── option sets for the property-feature dropdowns ────────────────────────────
const range = (start: number, end: number, step: number) => {
  const out: number[] = [];
  for (let v = start; v <= end + 1e-9; v += step) out.push(Number(v.toFixed(1)));
  return out;
};
const STORY_OPTIONS = range(1, 5, 0.5);
const BEDROOM_OPTIONS = range(1, 8, 1);
const BATH_OPTIONS = range(1, 8, 0.5);
const GARAGE_OPTIONS = range(0, 6, 1);

export const EditInputsDrawer: React.FC<EditInputsDrawerProps> = ({
  open,
  onClose,
  onForecast,
  params,
  setParams,
  resetDefaults,
  selectedClone,
  clusterName,
  clusterDescription,
}) => {
  const set = useCallback(
    <K extends keyof Params>(key: K) =>
      (v: Params[K]) =>
        setParams((p) => ({ ...p, [key]: v })),
    [setParams]
  );

  const handlePriceRange = useCallback(
    ([min = 0, max = 0]: number[]) => setParams((p) => ({ ...p, avgPriceMin: min, avgPriceMax: max })),
    [setParams]
  );
  const handleSqftRange = useCallback(
    ([min = 0, max = 0]: number[]) => setParams((p) => ({ ...p, squareFootMin: min, squareFootMax: max })),
    [setParams]
  );

  // Forecasted Absorption — editable placeholder; not yet wired to params.
  const [absorption, setAbsorption] = useState({ current: 2.9, m6: 2.9, m12: 2.9, m24: 2.9 });

  const resetPropertyFeatures = useCallback(() => {
    if (!selectedClone) return;
    const d = selectedClone.defaults;
    setParams((p) => ({
      ...p,
      avgPriceMin: d.avgPriceMin,
      avgPriceMax: d.avgPriceMax,
      squareFootMin: d.squareFootMin,
      squareFootMax: d.squareFootMax,
      stories: d.stories,
      bedrooms: d.bedrooms,
      baths: d.baths,
      garageSpaces: d.garageSpaces,
      garageType: d.garageType,
      city: d.city,
      amenities: [...(d.amenities ?? [])],
    }));
  }, [selectedClone, setParams]);

  const resetInterestRate = useCallback(() =>
    setParams((p) => ({
      ...p,
      interestRate: pluginConfig.defaults.interestRate,
      interestRateSixMonthChange: pluginConfig.defaults.interestRate,
      interestRateTwelveMonthChange: pluginConfig.defaults.interestRate,
      interestRateTwoYearChange: pluginConfig.defaults.interestRate,
    })), [setParams]);

  const resetAbsorption = useCallback(() =>
    setAbsorption({ current: 2.9, m6: 2.9, m12: 2.9, m24: 2.9 }), []);

  const resetNeighborhoodComposition = useCallback(() => {
    if (!selectedClone) return;
    const d = selectedClone.defaults;
    setParams((p) => ({
      ...p,
      availableLots: d.availableLots,
      initialSpecs: d.initialSpecs,
      preSolds: d.preSolds,
      activeComps: d.activeComps,
      specsToStart: d.specsToStart,
      perSpecsSold: d.perSpecsSold,
    }));
  }, [selectedClone, setParams]);

  const resetPriceChanges = useCallback(() => {
    if (!selectedClone) return;
    const d = selectedClone.defaults;
    setParams((p) => ({
      ...p,
      sixMonthPriceChange: d.sixMonthPriceChange,
      twelveMonthPriceChange: d.twelveMonthPriceChange,
      twoYearPriceChange: d.twoYearPriceChange,
    }));
  }, [selectedClone, setParams]);

  const pct = (v: number) => `${v.toFixed(3)}%`;
  const perMo = (v: number) => `${v.toFixed(1)}/mo`;

  if (!open) return null;

  return (
    <div
      style={{
        backgroundColor: cardBg,
        borderBottom: `1px solid ${borderColor}`,
      }}
    >
      {/* Slider thumb styling (track fill is applied inline per-slider). */}
      <style>{`
        .mf-range {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 999px;
          outline: none;
          cursor: pointer;
        }
        .mf-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--accent-color);
          border: 2px solid var(--card-bg);
          box-shadow: 0 0 0 1px var(--accent-color);
          cursor: pointer;
        }
        .mf-range::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border: 2px solid var(--card-bg);
          border-radius: 50%;
          background: var(--accent-color);
          cursor: pointer;
        }
      `}</style>
        {/* Two-row content grid */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.25rem",
            padding: "1.25rem",
          }}
        >
          {/* ── ROW 1: Property Features | Interest Rate + Absorption ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: "1.25rem", alignItems: "stretch" }}>
            {/* Property Features */}
            <SectionCard icon={<Home size={18} />} title="Property Features" onReset={resetPropertyFeatures} style={{ height: "100%" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem" }}>
                <PriceRangeControl
                  min={0}
                  max={1_000_000}
                  step={1}
                  values={[params.avgPriceMin, params.avgPriceMax]}
                  onChange={handlePriceRange}
                />
                <SquareFootRangeControl
                  min={500}
                  max={10_000}
                  step={50}
                  values={[params.squareFootMin, params.squareFootMax]}
                  onChange={handleSqftRange}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "0.75rem" }}>
                <NumberSelect label="Stories" value={params.stories} options={STORY_OPTIONS} onChange={set("stories")} />
                <NumberSelect label="Bedrooms" value={params.bedrooms} options={BEDROOM_OPTIONS} onChange={set("bedrooms")} />
                <NumberSelect label="Bathrooms" value={params.baths} options={BATH_OPTIONS} onChange={set("baths")} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "0.75rem" }}>
                <NumberSelect label="Garages" value={params.garageSpaces} options={GARAGE_OPTIONS} onChange={set("garageSpaces")} />
                <TextSelect
                  label="Garage Type"
                  value={GARAGE_TYPE_OPTIONS.includes(params.garageType as any) ? params.garageType : GARAGE_TYPE_OPTIONS[0]}
                  options={GARAGE_TYPE_OPTIONS}
                  onChange={set("garageType")}
                />
                <TextSelect
                  label="City"
                  value={CITY_OPTIONS.includes(params.city) ? params.city : CITY_OPTIONS[0]}
                  options={CITY_OPTIONS}
                  onChange={set("city")}
                />
              </div>

              <AmenityControl
                selectedAmenities={params.amenities}
                onChange={(updated) => setParams((p) => ({ ...p, amenities: updated }))}
                clusterName={clusterName}
                clusterDescription={clusterDescription}
              />
            </SectionCard>

            {/* ── RIGHT COLUMN: Interest Rate + Absorption side by side ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", alignItems: "stretch" }}>
              {/* Interest Rate Outlook */}
              <SectionCard icon={<TrendingUp size={18} />} title="Interest Rate Outlook" onReset={resetInterestRate} style={{ height: "100%" }}>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1 }}>
                  <SliderRow label="Current Rate" value={params.interestRate} display={pct(params.interestRate)} min={0} max={12} step={0.001} onChange={set("interestRate")} />
                  <SliderRow label="6-Month Rate" value={params.interestRateSixMonthChange} display={pct(params.interestRateSixMonthChange)} min={0} max={12} step={0.001} onChange={set("interestRateSixMonthChange")} />
                  <SliderRow label="12-Month Rate" value={params.interestRateTwelveMonthChange} display={pct(params.interestRateTwelveMonthChange)} min={0} max={12} step={0.001} onChange={set("interestRateTwelveMonthChange")} />
                  <SliderRow label="24-Month Rate" value={params.interestRateTwoYearChange} display={pct(params.interestRateTwoYearChange)} min={0} max={12} step={0.001} onChange={set("interestRateTwoYearChange")} />
                </div>
              </SectionCard>

              {/* Forecasted Absorption */}
              <SectionCard icon={<BarChart3 size={18} />} title="Forecasted Absorption" onReset={resetAbsorption} style={{ height: "100%" }}>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1 }}>
                  <SliderRow label="Current Absorption" value={absorption.current} display={perMo(absorption.current)} min={0} max={20} step={0.1} onChange={(v) => setAbsorption((a) => ({ ...a, current: v }))} />
                  <SliderRow label="6-Month Absorption" value={absorption.m6} display={perMo(absorption.m6)} min={0} max={20} step={0.1} onChange={(v) => setAbsorption((a) => ({ ...a, m6: v }))} />
                  <SliderRow label="12-Month Absorption" value={absorption.m12} display={perMo(absorption.m12)} min={0} max={20} step={0.1} onChange={(v) => setAbsorption((a) => ({ ...a, m12: v }))} />
                  <SliderRow label="24-Month Absorption" value={absorption.m24} display={perMo(absorption.m24)} min={0} max={20} step={0.1} onChange={(v) => setAbsorption((a) => ({ ...a, m24: v }))} />
                </div>
              </SectionCard>
            </div>
          </div>

          {/* ── ROW 2: Neighborhood Composition | Forecasted Price Changes ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: "1.25rem", alignItems: "stretch" }}>
            {/* Neighborhood Composition */}
            <SectionCard icon={<ClipboardList size={18} />} title="Neighborhood Composition" onReset={resetNeighborhoodComposition} style={{ height: "100%" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "0.75rem" }}>
                <NumberField label="Total Lots" value={params.availableLots} onChange={set("availableLots")} min={0} step={1} />
                <NumberField label="Initial Specs" value={params.initialSpecs} onChange={set("initialSpecs")} min={0} step={1} />
                <NumberField label="Initial Pre-Solds" value={params.preSolds} onChange={set("preSolds")} min={0} step={1} />
                <NumberField label="Active Comps" value={params.activeComps ?? 0} onChange={set("activeComps")} min={0} step={1} />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <span style={sectionLabel}>Additional Specs:</span>
                <span style={{ color: mutedText, fontSize: "0.85rem" }}>Start</span>
                <input
                  type="number"
                  min={0}
                  value={params.specsToStart}
                  onChange={(e) => set("specsToStart")(Number(e.target.value))}
                  style={{ ...numberInputStyle, width: "5rem" }}
                />
                <span style={{ color: mutedText, fontSize: "0.85rem" }}>per</span>
                <input
                  type="number"
                  min={1}
                  value={params.perSpecsSold}
                  onChange={(e) => set("perSpecsSold")(Number(e.target.value))}
                  style={{ ...numberInputStyle, width: "5rem" }}
                />
                <span style={{ color: mutedText, fontSize: "0.85rem" }}>Sold</span>
              </div>
            </SectionCard>

            {/* Forecasted Price Changes */}
            <SectionCard icon={<TrendingUp size={18} />} title="Forecasted Price Changes" onReset={resetPriceChanges} style={{ height: "100%" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "0.75rem" }}>
                <NumberField label="6-Mo Δ" value={params.sixMonthPriceChange} onChange={set("sixMonthPriceChange")} step={1} prefix="$" />
                <NumberField label="9-Mo Δ" value={params.nineMonthPriceChange} onChange={set("nineMonthPriceChange")} step={1} prefix="$" />
                <NumberField label="12-Mo Δ" value={params.twelveMonthPriceChange} onChange={set("twelveMonthPriceChange")} step={1} prefix="$" />
                <NumberField label="24-Mo Δ" value={params.twoYearPriceChange} onChange={set("twoYearPriceChange")} step={1} prefix="$" />
              </div>
            </SectionCard>
          </div>
        </div>

        {/* Drawer footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.85rem 1.25rem",
            borderTop: `1px solid ${borderColor}`,
            backgroundColor: cardBg,
          }}
        >
          {/* Left: Collapse Inputs */}
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              boxShadow: "none",
              padding: 0,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              color: accentColor,
              fontSize: "0.78rem",
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            <span style={{ borderBottom: `2px solid ${accentColor}`, paddingBottom: "1px" }}>
              Collapse Inputs ▲
            </span>
          </button>

          {/* Right: Reset + Apply & Recalculate */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button
              type="button"
              onClick={resetDefaults}
              style={{
                background: "transparent",
                border: `1px solid ${borderColor}`,
                borderRadius: "8px",
                padding: "0.55rem 1.1rem",
                color: textColor,
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Reset to Defaults
            </button>
            <button
              type="button"
              onClick={() => {
                setParams((p) => ({ ...p, cloneFlag: !p.cloneFlag }));
                onForecast();
              }}
              style={{
                background: "#FE7E66",
                border: "none",
                borderRadius: "8px",
                padding: "0.55rem 1.25rem",
                color: "#fff",
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              Apply &amp; Recalculate
              <span style={{ fontSize: "1rem", lineHeight: 1 }}>→</span>
            </button>
          </div>
        </div>
    </div>
  );
};
